// src/services/agentModelServiceFactory.ts

import { ModelService } from "../interfaces/modelService";
import { AgentAwareModelService } from "./agentAwareModelService";
import { chatModelService } from "./chatModelService";
import { createLogger } from "../utils/logger";

const log = createLogger("AgentModelServiceFactory");

/**
 * Agent Model Service Factory
 *
 * This factory manages the creation and lifecycle of agent-specific model services.
 * It ensures that each agent gets its own isolated service instance with its own
 * session management and state.
 *
 * Think of this like a car rental agency - when you need a car (service) for
 * a specific purpose (agent), it gives you the right vehicle with its own
 * mileage tracker (session) and fuel tank (state).
 *
 * Design principles:
 * 1. Each agent gets its own service instance (isolation)
 * 2. Services are reused within the same session (efficiency)
 * 3. Services are properly cleaned up when switching agents (no leaks)
 */
export class AgentModelServiceFactory {
  // Cache of agent-specific services
  private services: Map<string, ModelService> = new Map();

  // Track the current active agent
  private currentAgentId: string | null = null;

  /**
   * Get or create a model service for a specific agent
   *
   * This method ensures each agent has its own isolated service instance.
   * Services are preserved when switching agents to maintain conversation history.
   */
  getServiceForAgent(agentId: string): ModelService {
    // Check if we're switching agents (for logging purposes only)
    if (this.currentAgentId && this.currentAgentId !== agentId) {
      log.info("Agent switch detected, preserving previous service", {
        fromAgent: this.currentAgentId,
        toAgent: agentId,
        totalServices: this.services.size,
        preservedSessions: Array.from(this.services.keys()),
      });
    }

    // Update current agent
    this.currentAgentId = agentId;

    // Check if we already have a service for this agent
    let service = this.services.get(agentId);

    if (!service) {
      // Create a new service for this agent
      service = this.createServiceForAgent(agentId);
      this.services.set(agentId, service);

      log.info("Created new model service for agent", {
        agentId,
        totalServices: this.services.size,
      });
    }

    return service;
  }

  /**
   * Create a new service instance for a specific agent
   *
   * In the future, we might have agent-specific service implementations.
   * For now, all agents use the same AgentAwareModelService class.
   */
  private createServiceForAgent(agentId: string): ModelService {
    // For backward compatibility, we could return the original chatModelService
    // for red-line-analyzer if needed. But for consistency, we'll use
    // AgentAwareModelService for all agents.

    return new AgentAwareModelService(agentId);
  }

  /**
   * Explicitly clean up resources for a specific agent
   *
   * This method is now public to allow explicit cleanup when needed.
   * It is no longer called automatically on agent switch to preserve
   * conversation history across agent changes.
   *
   * @param agentId - The agent ID to clean up
   */
  public cleanupAgent(agentId: string): void {
    const service = this.services.get(agentId);

    if (service) {
      // Clear the session to ensure clean state
      if (service.clearSession) {
        service.clearSession();
      }

      // Cancel any ongoing operations
      if (service.cancelStreaming) {
        service.cancelStreaming();
      }

      // Remove from cache
      this.services.delete(agentId);

      log.info("Cleaned up service for agent", {
        agentId,
        remainingServices: this.services.size,
      });
    }
  }

  /**
   * Clean up all agent services
   *
   * This method should be called when:
   * - The application is unmounting
   * - User explicitly requests to clear all conversations
   * - Memory needs to be freed (e.g., too many agents loaded)
   *
   * Note: This will clear ALL conversation history across ALL agents
   */
  cleanupAll(): void {
    log.info("Cleaning up all agent services", {
      serviceCount: this.services.size,
    });

    // Clean up each service - using Array.from for compatibility
    Array.from(this.services.entries()).forEach(([agentId, service]) => {
      if (service.clearSession) {
        service.clearSession();
      }
      if (service.cancelStreaming) {
        service.cancelStreaming();
      }
    });

    // Clear the cache
    this.services.clear();
    this.currentAgentId = null;
  }

  /**
   * Get the current active agent ID
   */
  getCurrentAgentId(): string | null {
    return this.currentAgentId;
  }

  /**
   * Check if a service exists for an agent
   */
  hasServiceForAgent(agentId: string): boolean {
    return this.services.has(agentId);
  }

  /**
   * Get all active services (for debugging and monitoring)
   */
  getActiveServices(): Map<string, ModelService> {
    return new Map(this.services);
  }

  /**
   * Get session info for all agents
   */
  getAllAgentSessions(): { agentId: string; hasSession: boolean }[] {
    return Array.from(this.services.entries()).map(([agentId, service]) => ({
      agentId,
      hasSession:
        service instanceof AgentAwareModelService ? service.getSessionId() !== null : true,
    }));
  }
}

// Export singleton instance
export const agentModelServiceFactory = new AgentModelServiceFactory();
